/**
 * Derivations that must give the same answer on the server and in the browser.
 *
 * A feature has no lane column. Its lane is derived from its tasks, because a
 * stored one drifts the moment a task moves and nobody remembers to update it.
 * The client dashboard and the owner board both render feature lanes, so this
 * runs in both places and must be pure.
 */
import { LANES } from './enums.js';
import type { Lane } from './enums.js';
import type { FeatureRollup } from './types.js';

export interface TaskLike {
  lane: Lane;
  storyPoints: number | null;
  isBlocked: boolean;
}

/**
 * The lane a feature reports.
 *
 * Furthest-along-wins, with one exception: a feature is only `completed` when
 * every task is. Anything else would tell a client a feature was done while
 * work remained in it, which is the single most expensive thing this screen
 * could get wrong.
 */
export function deriveFeatureLane(tasks: readonly TaskLike[]): Lane {
  if (tasks.length === 0) return 'backlog';
  if (tasks.every((t) => t.lane === 'completed')) return 'completed';
  if (tasks.some((t) => t.lane === 'in_progress')) return 'in_progress';
  if (tasks.some((t) => t.lane === 'next_up')) return 'next_up';
  return 'backlog';
}

/**
 * Progress, 0-1.
 *
 * Points-weighted when any task carries an estimate, task-count-weighted
 * otherwise. Unestimated tasks in a partly-estimated feature count as zero
 * points, which understates progress rather than overstating it - the safer
 * direction when the number is shown to the person paying for the work.
 */
export function rollUpTasks(tasks: readonly TaskLike[]): FeatureRollup {
  const taskCount = tasks.length;
  const completed = tasks.filter((t) => t.lane === 'completed');
  const totalPoints = tasks.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);
  const completedPoints = completed.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);

  const progress =
    taskCount === 0
      ? 0
      : totalPoints > 0
        ? completedPoints / totalPoints
        : completed.length / taskCount;

  return {
    lane: deriveFeatureLane(tasks),
    taskCount,
    completedCount: completed.length,
    blockedCount: tasks.filter((t) => t.isBlocked).length,
    totalPoints: round1(totalPoints),
    completedPoints: round1(completedPoints),
    progress: Math.min(1, Math.max(0, progress)),
  };
}

/** Lane totals in board order, including lanes with nothing in them. */
export function laneTotals(tasks: readonly TaskLike[]): { lane: Lane; taskCount: number; points: number }[] {
  return LANES.map((lane) => {
    const inLane = tasks.filter((t) => t.lane === lane);
    return {
      lane,
      taskCount: inLane.length,
      points: round1(inLane.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0)),
    };
  });
}

/**
 * Story points are half-steps, so sums land on halves. Floating-point addition
 * of 0.5 is exact, but 1.5 + 1.5 + 1.5 is not always what a reader expects to
 * see rendered, and a capacity bar reading "4.499999999999999 of 5" is a bug
 * report. One decimal place, once, here.
 */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export { round1 as roundPoints };
